<script lang="ts" setup>
import ResizeObserver from 'resize-observer-polyfill';
import { computed, onBeforeUnmount, onMounted, provide, reactive, ref } from 'vue';

const props = defineProps({
  incomingWrapperStyles: { type: Object, default: () => {} },
  lightTheme: { type: Boolean, default: false },
  position: { type: String, default: 'right' },
});

const menuButton = ref<HTMLElement>();
const menuOpen = ref(false);
const state = reactive({
  buttonBounds: {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
    x: 0,
    y: 0,
  },
  documentSizes: {
    width: 0,
    height: 0,
  },
});

const getScrollAreaMaxHeight = (top: string, documentHeight: number) => `calc(${documentHeight}px - ${top})`;
const arrowOffset = {
  x: 18,
  y: 18,
};

const wrapperStyles = computed(() => {
  let position: Dictionary<string>;

  if (props.position === 'left') {
    position = {
      left: '0px',
    };
  } else {
    position = {
      right: '0px',
    };
  }

  return {
    top: `calc(${state.buttonBounds.height}px + 5px)`,
    ...position,
    ...props.incomingWrapperStyles,
  };
});

const scrollStyles = computed(() => ({ maxHeight: getScrollAreaMaxHeight(`${state.buttonBounds.bottom + arrowOffset.y}px`, state.documentSizes.height) }));

const computingSizes = (el: HTMLElement) => {
  state.buttonBounds = el.getBoundingClientRect();
  state.documentSizes.width = document.documentElement.clientWidth;
  state.documentSizes.height = document.documentElement.clientHeight;
};

const recalculate = () => {
  if (menuButton.value) {
    computingSizes(menuButton.value);
  }
};

const openMenu = () => {
  recalculate();
  menuOpen.value = !menuOpen.value;
};

const closeMenu = () => {
  menuOpen.value = false;
};

const bodyObserver = new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
  entries.forEach(() => {
    recalculate();
  });
});

bodyObserver.observe(document.body);

provide('closeMenu', closeMenu);

onMounted(() => {
  recalculate();
});

onBeforeUnmount(() => {
  if (bodyObserver) {
    bodyObserver.disconnect();
  }
});
</script>

<template>
  <div class="header-menu">
    <div
      ref="menuButton"
      class="header-menu__activator"
      @click="openMenu"
    >
      <slot name="default" />
    </div>
    <transition name="fade">
      <div
        v-if="menuOpen"
        class="header-menu__overlay"
      />
    </transition>
    <transition
      name="fade-scale"
      mode="in-out"
    >
      <div
        v-if="menuOpen"
        :class="['header-menu__overlay', { 'header-menu__overlay--transparent': true }]"
        @click.self="closeMenu()"
      />
    </transition>
    <transition
      name="fade-scale"
      mode="in-out"
    >
      <div
        v-if="menuOpen"
        :style="wrapperStyles"
        :class="['xot-menu__wrapper', 'header-menu__wrapper', { open: menuOpen, [position]: true, 'header-menu__wrapper--light': lightTheme }]"
      >
        <div class="header-menu__items">
          <ot-scroll
            class="header-menu__scroll-area"
            :style="scrollStyles"
          >
            <slot
              name="menu-content"
              :close-menu="closeMenu"
            />
          </ot-scroll>
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss">
.header-menu {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;

  &__scroll-area {
    position: relative;
  }

  &__activator {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &::before {
      $offset: 10px;

      position: absolute;
      top: calc(-1 * $offset);
      left: calc(-1 * $offset);
      width: calc(100% + 2 * $offset);
      height: calc(100% + 2 * $offset);
      content: '';
      cursor: pointer;
    }
  }

  &__overlay {
    position: fixed;
    z-index: var(--menu-overlay-z-index);
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    /* stylelint-disable-next-line color-function-notation */
    background-color: rgba(var(--v-theme-overlay-2), var(--v-overlay-2));
    transition: opacity 0.3s;
    will-change: auto;

    &--transparent {
      background: transparent;
    }

    &.fade-enter-active,
    &.fade-leave-active {
      opacity: 1;
      transition-timing-function: ease-out;
    }

    &.fade-enter-from,
    &.fade-leave-to {
      opacity: 0;
    }
  }

  &__wrapper {
    position: absolute;
    z-index: calc(var(--on-top-of-all-z-index) + var(--menu-wrapper-z-index));
    background-color: rgb(var(--v-theme-elevation-1));

    &--light {
      background-color: var(--elevation-light-0);
    }
  }

  &__wrapper,
  &__scroll-area {
    border-radius: 12px;
  }

  .fade-scale-enter-active,
  .fade-scale-leave-active {
    transition: opacity 0.2s cubic-bezier(0.25, 1.26, 0.72, 1.01);

    .xot-menu__wrapper {
      transition: transform 0.4s cubic-bezier(0.25, 1.26, 0.72, 1.01);

      &.left {
        transform-origin: 18px -8px;
      }

      &.right {
        transform-origin: calc(100% - 18px) -8px;
      }
    }
  }

  .fade-scale-enter-from,
  .fade-scale-leave-to {
    opacity: 0;

    .xot-menu__wrapper {
      transform: scale(0);
    }
  }

  .fade-scale-enter-to,
  .fade-scale-leave-from {
    opacity: 1;

    .xot-menu__wrapper {
      transform: scale(1);
    }
  }
}
</style>
